import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import type { OrganizationData, OrganizationMembership, OrganizationUserRoles } from '~/types/organization';
import type { UserData } from '~/types/user';

interface BaseTeamMemberEventProperties {
  // organization context
  organization_id: number;
  organization_slug: string;
  organization_name: string;

  // actor context (who performed the action)
  actor_user_id?: string;
  actor_username?: string;
  actor_email?: string;
  actor_role?: OrganizationUserRoles;

  // team context
  team_size?: number;
  team_tier?: number;
}

interface TargetMemberProperties {
  // target member context (who was affected)
  target_user_id?: number;
  target_username?: string;
  target_email?: string;
  target_pending?: boolean;
  target_with_membership?: boolean;
}

interface BatchMemberProperties {
  member_count: number;
  member_usernames?: string[];
  member_emails?: string[];
}

function getBaseEventProperties(
  organization: OrganizationData,
  actor?: UserData,
  teamMembers?: OrganizationMembership[],
): BaseTeamMemberEventProperties {
  return {
    organization_id: organization.id,
    organization_slug: organization.slug,
    organization_name: organization.name,
    actor_user_id: actor?.id,
    actor_username: actor?.username,
    actor_email: actor?.email,
    actor_role: organization.role,
    team_size: teamMembers?.length,
    team_tier: organization.billing?.tier,
  };
}

function getMemberProperties(member: OrganizationMembership): TargetMemberProperties {
  return {
    target_user_id: member.user.id,
    target_username: member.user.username,
    target_email: member.user.email,
    target_pending: member.user.pending,
    target_with_membership: member.withMembership,
  };
}

export function trackTeamMemberInvited(
  organization: OrganizationData,
  invitees: { email?: string; username?: string; role: OrganizationUserRoles }[],
  actor?: UserData,
  teamMembers?: OrganizationMembership[],
) {
  const baseProperties = getBaseEventProperties(organization, actor, teamMembers);

  invitees.forEach((invitee) => {
    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.TeamMemberInvited,
        properties: {
          ...baseProperties,
          target_email: invitee.email,
          target_username: invitee.username,
          target_role: invitee.role,
          invitation_method: invitee.email ? 'email' : 'username',
        },
      },
    });
  });
}

export function trackTeamMemberInvitationAccepted(
  organization: OrganizationData,
  member?: OrganizationMembership,
  actor?: UserData,
) {
  const baseProperties = getBaseEventProperties(organization, actor);
  const memberProperties = member ? getMemberProperties(member) : {};

  sendAnalyticsEvent({
    action: AnalyticsAction.Track,
    payload: {
      event: AnalyticsTrackEvent.TeamMemberInvitationAccepted,
      properties: {
        ...baseProperties,
        ...memberProperties,
      },
    },
  });
}

export function trackTeamMemberInvitationDeclined(
  organization: OrganizationData,
  member?: OrganizationMembership,
  actor?: UserData,
) {
  const baseProperties = getBaseEventProperties(organization, actor);
  const memberProperties = member ? getMemberProperties(member) : {};

  sendAnalyticsEvent({
    action: AnalyticsAction.Track,
    payload: {
      event: AnalyticsTrackEvent.TeamMemberInvitationDeclined,
      properties: {
        ...baseProperties,
        ...memberProperties,
      },
    },
  });
}

export function trackTeamMemberInvitationResent(
  organization: OrganizationData,
  members: OrganizationMembership[],
  actor?: UserData,
  teamMembers?: OrganizationMembership[],
) {
  const baseProperties = getBaseEventProperties(organization, actor, teamMembers);

  const batchProperties: BatchMemberProperties = {
    member_count: members.length,
    member_usernames: members.map((m) => m.user.username),
    member_emails: members.map((m) => m.user.email),
  };

  sendAnalyticsEvent({
    action: AnalyticsAction.Track,
    payload: {
      event: AnalyticsTrackEvent.TeamMemberInvitationResent,
      properties: {
        ...baseProperties,
        ...batchProperties,
        is_batch: members.length > 1,
      },
    },
  });
}

export function trackTeamMemberRoleChanged(
  organization: OrganizationData,
  members: { member: OrganizationMembership; oldRole: OrganizationUserRoles; newRole: OrganizationUserRoles }[],
  actor?: UserData,
  teamMembers?: OrganizationMembership[],
) {
  const baseProperties = getBaseEventProperties(organization, actor, teamMembers);

  members.forEach(({ member, oldRole, newRole }) => {
    const memberProperties = getMemberProperties(member);

    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.TeamMemberRoleChanged,
        properties: {
          ...baseProperties,
          ...memberProperties,
          old_role: oldRole,
          new_role: newRole,
          is_promotion: getRoleLevel(newRole) > getRoleLevel(oldRole),
          is_batch: members.length > 1,
        },
      },
    });
  });
}

export function trackTeamMemberUpgraded(
  organization: OrganizationData,
  members: OrganizationMembership[],
  actor?: UserData,
  teamMembers?: OrganizationMembership[],
) {
  const baseProperties = getBaseEventProperties(organization, actor, teamMembers);

  const batchProperties: BatchMemberProperties = {
    member_count: members.length,
    member_usernames: members.map((m) => m.user.username),
    member_emails: members.map((m) => m.user.email),
  };

  sendAnalyticsEvent({
    action: AnalyticsAction.Track,
    payload: {
      event: AnalyticsTrackEvent.TeamMemberUpgraded,
      properties: {
        ...baseProperties,
        ...batchProperties,
        is_batch: members.length > 1,
      },
    },
  });
}

export function trackTeamMemberDowngraded(
  organization: OrganizationData,
  members: OrganizationMembership[],
  actor?: UserData,
  teamMembers?: OrganizationMembership[],
) {
  const baseProperties = getBaseEventProperties(organization, actor, teamMembers);

  const batchProperties: BatchMemberProperties = {
    member_count: members.length,
    member_usernames: members.map((m) => m.user.username),
    member_emails: members.map((m) => m.user.email),
  };

  sendAnalyticsEvent({
    action: AnalyticsAction.Track,
    payload: {
      event: AnalyticsTrackEvent.TeamMemberDowngraded,
      properties: {
        ...baseProperties,
        ...batchProperties,
        is_batch: members.length > 1,
      },
    },
  });
}

export function trackTeamMemberRemoved(
  organization: OrganizationData,
  members: OrganizationMembership[],
  actor?: UserData,
  teamMembers?: OrganizationMembership[],
) {
  const baseProperties = getBaseEventProperties(organization, actor, teamMembers);

  const batchProperties: BatchMemberProperties = {
    member_count: members.length,
    member_usernames: members.map((m) => m.user.username),
    member_emails: members.map((m) => m.user.email),
  };

  sendAnalyticsEvent({
    action: AnalyticsAction.Track,
    payload: {
      event: AnalyticsTrackEvent.TeamMemberRemoved,
      properties: {
        ...baseProperties,
        ...batchProperties,
        is_batch: members.length > 1,
        removed_pending_members: members.filter((m) => m.user.pending).length,
        removed_active_members: members.filter((m) => !m.user.pending).length,
      },
    },
  });
}

export function trackTeamMemberUpgradeRequested(
  organization: OrganizationData,
  member: { id: number; email: string; username: string },
  actor?: UserData,
) {
  const baseProperties = getBaseEventProperties(organization, actor);

  sendAnalyticsEvent({
    action: AnalyticsAction.Track,
    payload: {
      event: AnalyticsTrackEvent.TeamMemberUpgradeRequested,
      properties: {
        ...baseProperties,
        target_user_id: member.id,
        target_username: member.username,
        target_email: member.email,
      },
    },
  });
}

// helper function to determine role hierarchy for promotion/demotion detection
function getRoleLevel(role: OrganizationUserRoles): number {
  switch (role) {
    case 'admin': {
      return 3;
    }
    case 'billing_admin': {
      return 2;
    }
    case 'member': {
      return 1;
    }
    default: {
      return 0;
    }
  }
}
